import React, { useState, useEffect } from 'react';
import { X, Save, Building } from 'lucide-react';
import companyService from '../../services/companyService';
import alertService from '../../services/alertService';
import {
  EmpresaResponseDTO,
  EmpresaRequestDTO,
  CompanyFormData
} from '../../types/company';

interface CompanyModalProps {
  company?: EmpresaResponseDTO | null;
  onClose: () => void;
  onSave: () => void;
}

const CompanyModal: React.FC<CompanyModalProps> = ({ company, onClose, onSave }) => {
  const [formData, setFormData] = useState<CompanyFormData>({
    nomeFantasia: '',
    cnpj: '',
    razaoSocial: '',
    codigoIdentificador: '',
    isMatriz: false,
    ativo: true
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!company;

  // Preencher formulário quando editando
  useEffect(() => {
    if (company) {
      setFormData({
        nomeFantasia: company.nomeFantasia,
        cnpj: company.cnpj,
        razaoSocial: company.razaoSocial,
        codigoIdentificador: company.codigoIdentificador,
        isMatriz: company.isMatriz,
        ativo: company.ativo
      });
    }
  }, [company]);

  // Validação do formulário
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nomeFantasia.trim()) {
      newErrors.nomeFantasia = 'Nome fantasia é obrigatório';
    }

    if (!formData.razaoSocial.trim()) {
      newErrors.razaoSocial = 'Razão social é obrigatória';
    }

    if (!formData.cnpj.trim()) {
      newErrors.cnpj = 'CNPJ é obrigatório';
    } else if (!companyService.validateCNPJ(formData.cnpj)) {
      newErrors.cnpj = 'CNPJ inválido';
    }

    if (!formData.codigoIdentificador.trim()) {
      newErrors.codigoIdentificador = 'Código identificador é obrigatório';
    } else if (formData.codigoIdentificador.length > 10) {
      newErrors.codigoIdentificador = 'Código identificador deve ter no máximo 10 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manipular mudanças nos campos
  const handleInputChange = (field: keyof CompanyFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Formatação automática do CNPJ
  const handleCNPJChange = (value: string) => {
    // Remove caracteres não numéricos
    const cleanValue = value.replace(/[^\d]/g, '');
    
    // Aplica formatação
    let formattedValue = cleanValue;
    if (cleanValue.length > 2) {
      formattedValue = cleanValue.replace(/(\d{2})(\d{0,3})(\d{0,3})(\d{0,4})(\d{0,2})/, (match, p1, p2, p3, p4, p5) => {
        let result = p1;
        if (p2) result += '.' + p2;
        if (p3) result += '.' + p3;
        if (p4) result += '/' + p4;
        if (p5) result += '-' + p5;
        return result;
      });
    }
    
    // Limita a 18 caracteres (formato completo: XX.XXX.XXX/XXXX-XX)
    if (formattedValue.length <= 18) {
      handleInputChange('cnpj', formattedValue);
    }
  };

  // Submeter formulário
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const requestData: EmpresaRequestDTO = {
        nomeFantasia: formData.nomeFantasia.trim(),
        cnpj: companyService.cleanCNPJ(formData.cnpj),
        razaoSocial: formData.razaoSocial.trim(),
        codigoIdentificador: formData.codigoIdentificador.trim(),
        isMatriz: formData.isMatriz,
        ativo: formData.ativo
      };

      if (isEditing && company) {
        await companyService.updateCompany(company.id, requestData);
        alertService.success('Empresa atualizada com sucesso!');
      } else {
        await companyService.createCompany(requestData);
        alertService.success('Empresa criada com sucesso!');
      }

      onSave();
    } catch (error: any) {
      console.error('Erro ao salvar empresa:', error);
      
      // Tratar erros específicos da API
      if (error.response?.status === 400) {
        const apiErrors = error.response.data?.errors || [];
        if (apiErrors.length > 0) {
          alertService.error(apiErrors.join(', '));
        } else {
          alertService.error('Dados inválidos. Verifique os campos e tente novamente.');
        }
      } else if (error.response?.status === 409) {
        alertService.error('CNPJ já cadastrado no sistema.');
      } else {
        alertService.error('Erro ao salvar empresa. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white w-full h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-white px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <Building className="h-6 w-6 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                {company ? 'Editar Empresa' : 'Nova Empresa'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Conteúdo do formulário */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="h-full flex flex-col">
            <div className="flex-1 px-6 py-6 space-y-6">
              {/* Seção de Dados Básicos */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                  Dados Básicos
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Fantasia *
                    </label>
                    <input
                      type="text"
                      name="nomeFantasia"
                      value={formData.nomeFantasia}
                      onChange={(e) => handleInputChange('nomeFantasia', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        errors.nomeFantasia ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Digite o nome fantasia"
                      maxLength={255}
                    />
                    {errors.nomeFantasia && (
                      <p className="mt-1 text-sm text-red-600">{errors.nomeFantasia}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Razão Social *
                    </label>
                    <input
                      type="text"
                      name="razaoSocial"
                      value={formData.razaoSocial}
                      onChange={(e) => handleInputChange('razaoSocial', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        errors.razaoSocial ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Digite a razão social"
                    />
                    {errors.razaoSocial && (
                      <p className="mt-1 text-sm text-red-600">{errors.razaoSocial}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CNPJ *
                    </label>
                    <input
                      type="text"
                      name="cnpj"
                      value={formData.cnpj}
                      onChange={(e) => handleCNPJChange(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        errors.cnpj ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="XX.XXX.XXX/XXXX-XX"
                    />
                    {errors.cnpj && (
                      <p className="mt-1 text-sm text-red-600">{errors.cnpj}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Código Identificador *
                    </label>
                    <input
                      type="text"
                      name="codigoIdentificador"
                      value={formData.codigoIdentificador}
                      onChange={(e) => handleInputChange('codigoIdentificador', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                        errors.codigoIdentificador ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Digite o código identificador"
                      maxLength={10}
                    />
                    {errors.codigoIdentificador && (
                      <p className="mt-1 text-sm text-red-600">{errors.codigoIdentificador}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Seção de Configurações */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                  Configurações
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isMatriz"
                      checked={formData.isMatriz}
                      onChange={(e) => handleInputChange('isMatriz', e.target.checked)}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isMatriz" className="ml-2 block text-sm text-gray-900">
                      É Matriz
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="ativo"
                      checked={formData.ativo}
                      onChange={(e) => handleInputChange('ativo', e.target.checked)}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <label htmlFor="ativo" className="ml-2 block text-sm text-gray-900">
                      Ativo
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Salvando...
                    </div>
                  ) : (
                    isEditing ? 'Atualizar' : 'Criar'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompanyModal;